import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Copy, Eye, MoreHorizontal, Pause, Pencil, Play, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '@/shared/components/confirm';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { useEmailMarketing } from '../model/context';
import { useEmailMarketingPaths } from '../model/paths';
import {
  ACTION_LABEL,
  actionsForStatus,
  marketingErrorToast,
  type CampaignActionId,
  type CampaignStatus,
} from '@/features/marketing-shared';

const ACTION_ICON: Record<CampaignActionId, typeof Eye> = {
  edit: Pencil,
  view: Eye,
  results: Eye,
  duplicate: Copy,
  delete: Trash2,
  pause: Pause,
  resume: Play,
  cancel: XCircle,
};

export function CampaignActions({
  campaign,
}: {
  campaign: { id: string; name: string; status: CampaignStatus };
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const paths = useEmailMarketingPaths();
  const email = useEmailMarketing();
  const [busy, setBusy] = useState(false);
  const actions = actionsForStatus(campaign.status);
  const detailPath = paths.detail(campaign.id);
  const editPath = paths.edit(campaign.id);
  const listPath = paths.list;

  const perform = async (work: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await work();
    } catch (error) {
      const { message, description } = marketingErrorToast(error, fallback);
      toast.error(message, { description });
    } finally {
      setBusy(false);
    }
  };

  const run = (action: CampaignActionId) => {
    if (action === 'edit') {
      navigate(editPath);
      return;
    }
    if (action === 'view' || action === 'results') {
      navigate(detailPath);
      return;
    }
    if (action === 'duplicate') {
      void perform(async () => {
        const copy = await email.duplicateCampaign(campaign.id);
        toast.success('Campanha duplicada como rascunho.');
        navigate(paths.edit(copy.id));
      }, 'Não foi possível duplicar a campanha.');
      return;
    }
    if (action === 'pause') {
      void perform(async () => {
        await email.pauseCampaign(campaign.id);
        toast.success('Campanha pausada.', { description: 'O que já foi aceito pelo provedor não volta; o próximo lote não sai.' });
      }, 'Não foi possível pausar a campanha.');
      return;
    }
    if (action === 'resume') {
      void perform(async () => {
        await email.resumeCampaign(campaign.id);
        toast.success('Campanha retomada.');
      }, 'Não foi possível retomar a campanha.');
      return;
    }
    if (action === 'cancel') {
      void confirm({
        title: 'Cancelar envio',
        description: `Cancelar "${campaign.name}"? O que ainda não saiu volta para o saldo; e-mails já aceitos pelo provedor não são recolhidos.`,
        confirmLabel: 'Cancelar envio',
        variant: 'destructive',
      }).then((ok) => {
        if (!ok) return;
        void perform(async () => {
          await email.cancelCampaign(campaign.id);
          toast.success('Campanha cancelada.');
        }, 'Não foi possível cancelar a campanha.');
      });
      return;
    }
    void confirm({
      title: 'Excluir rascunho',
      description: `Excluir "${campaign.name}"? Só rascunhos podem ser excluídos.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    }).then((ok) => {
      if (!ok) return;
      void perform(async () => {
        await email.removeCampaign(campaign.id);
        toast.success('Rascunho excluído.');
        navigate(listPath);
      }, 'Não foi possível excluir o rascunho.');
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Ações da campanha ${campaign.name}`}
          loading={busy}
          disabled={busy}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action) => {
          const Icon = ACTION_ICON[action];
          return (
            <DropdownMenuItem key={action} destructive={action === 'delete' || action === 'cancel'} onSelect={() => run(action)}>
              <Icon />
              {ACTION_LABEL[action]}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function BackToCampaigns() {
  const paths = useEmailMarketingPaths();
  return (
    <Button variant="ghost" size="icon-sm" className="-ml-2" asChild>
      <Link to={paths.list} aria-label="Voltar para campanhas">
        <ChevronLeft />
      </Link>
    </Button>
  );
}
